import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * useSiteConfig — fetch-once hook for /api/site-config. Returns an object with
 * sensible fallbacks so the first render never shows blanks. Exposed fields:
 *   superhost_since, total_stays_hosted, five_star_reviews, airbnb_markup_pct,
 *   instagram_handle, whatsapp_number, whatsapp_display, privacy_html, terms_html.
 */
const DEFAULTS = {
  superhost_since: "2022-11-01",
  total_stays_hosted: 2580,
  // Trust strip values: real anchor numbers so the SSR'd HTML and first
  // paint never show 0 to crawlers or users. Auto-recomputed from the
  // reviews collection once the page hydrates.
  five_star_reviews: 500,
  // Anchor for the "Average rating" trust pill — last computed across
  // all properties. Falls back here if the reviews aggregation hasn't
  // run yet (e.g. on a cold render before hydration).
  average_rating: 4.86,
  airbnb_markup_pct: 15,
  website_markup_pct: 0,
  // Live count of currently listed properties — drives copy like "Ten stays"
  // / "10+ homes" across About, Guest Love and Shoots so adding/removing a
  // listing automatically refreshes the marketing pages.
  active_properties_count: 10,
  privacy_html: "",
  terms_html: "",
  instagram_handle: "vyagram.experiences",
  whatsapp_number: "919355012999",
  whatsapp_display: "+91 93550 12999",
  // Single source-of-truth for support email — referenced by Footer,
  // Contact page, Policies, /privacy etc. Admin can override via site-config.
  support_email: "stay@vyagram.com",
  ga_measurement_id: "",
  meta_pixel_id: "",
  stay_policy_html: "",
  shoot_media: [],
  shoot_intro_html: "",
  favicon_url: "",
};

let cache = null;
let inflight = null;

export default function useSiteConfig() {
  const [cfg, setCfg] = useState(cache || DEFAULTS);
  useEffect(() => {
    if (cache) return;
    if (!inflight) {
      inflight = api
        .get("/site-config")
        .then(({ data }) => {
          cache = { ...DEFAULTS, ...data };
          return cache;
        });
    }
    inflight.then((data) => setCfg(data)).catch(() => setCfg(DEFAULTS));
  }, []);
  return cfg;
}

// Derived helpers for common consumption sites
export const instagramUrl = (handle) =>
  `https://instagram.com/${(handle || DEFAULTS.instagram_handle).replace(/^@/, "")}`;
export const whatsappLinkFromCfg = (cfg, message = "Hi Vyagram, I'd like to plan a stay.") => {
  const num = (cfg?.whatsapp_number || DEFAULTS.whatsapp_number).replace(/[^\d]/g, "");
  return `https://wa.me/${num}?text=${encodeURIComponent(message)}`;
};
